import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Type, Smile, RotateCcw, MessageSquare, Home, Globe, Monitor } from 'lucide-react';
import { useAppearance } from '../hooks/useAppearance.js';

export default function AppearanceSettingsModal({ onClose }) {
    const { appearance, saveSettings } = useAppearance();
    // Local State for smooth sliding (Buffered)
    const [localAppearance, setLocalAppearance] = useState(appearance);

    // Tracks if we have unsaved changes to show UI feedback if needed
    const [hasChanges, setHasChanges] = useState(false);

    const [confirmReset, setConfirmReset] = useState(null);

    const updateLocal = (scope, key, value) => {
        setLocalAppearance(prev => ({
            ...prev,
            [scope]: {
                ...prev[scope],
                [key]: value
            }
        }));
        setHasChanges(true);
    };

    const handleSave = () => {
        saveSettings(localAppearance);
        onClose();
    };

    const handleReset = (scope) => {
        // We need default values from hook/constants, but they aren't exported.
        // We can reconstruct them or use a helper. 
        // Ideally hook should provide defaults, but hardcoding for now to match hook logic:
        const DEFAULTS = {
            feed: { fontSize: 24, emoteSize: 48 },
            chat: { fontSize: 16, emoteSize: 32 },
            global: { fontSize: 16, emoteSize: 32 }
        };

        let newSettings;
        if (scope === 'all') {
            newSettings = DEFAULTS;
        } else {
            newSettings = {
                ...localAppearance,
                [scope]: DEFAULTS[scope]
            };
        }

        // Update Local State (Immediate UI Feedback)
        setLocalAppearance(newSettings);
        // Commit immediately (Reset implies action)
        saveSettings(newSettings);

        setConfirmReset(null);
        setHasChanges(false);
    };

    return createPortal(
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 99999,
            background: 'var(--backdrop)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'fadeIn 0.2s ease-out'
        }} onClick={(e) => { if (e.target === e.currentTarget && !confirmReset) onClose(); }}>
            <div className="glass-panel" style={{
                width: '500px',
                maxWidth: '90vw',
                maxHeight: '85vh',
                overflowY: 'auto',
                padding: '25px',
                background: 'var(--bg-card)',
                border: '1px solid var(--primary)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                borderRadius: '16px',
                position: 'relative',
                display: 'flex', flexDirection: 'column'
            }}>
                <button onClick={onClose} style={{
                    position: 'absolute', top: '15px', right: '15px',
                    background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer'
                }}>
                    <X size={24} />
                </button>

                <h2 style={{ fontSize: '20px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Monitor size={24} color="var(--primary)" /> Display Settings
                </h2>

                <RenderSection
                    title="Feed Display"
                    icon={Home}
                    scope="feed"
                    color="var(--primary)"
                    localAppearance={localAppearance}
                    updateLocal={updateLocal}
                    setConfirmReset={setConfirmReset}
                />
                <RenderSection
                    title="Chat Display"
                    icon={MessageSquare}
                    scope="chat"
                    color="#10b981"
                    localAppearance={localAppearance}
                    updateLocal={updateLocal}
                    setConfirmReset={setConfirmReset}
                />
                <RenderSection
                    title="Global Pages"
                    icon={Globe}
                    scope="global"
                    color="#ec4899"
                    localAppearance={localAppearance}
                    updateLocal={updateLocal}
                    setConfirmReset={setConfirmReset}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '20px', borderTop: '1px solid var(--border)' }}>
                    <button
                        onClick={() => setConfirmReset('all')}
                        className="btn-ghost"
                        style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', color: '#ef4444' }}
                    >
                        <RotateCcw size={14} /> Reset ALL Defaults
                    </button>
                    <button onClick={handleSave} className="btn-primary" style={{ padding: '8px 20px' }}>
                        Done
                    </button>
                </div>

                {/* CONFIRMATION POPUP */}
                {confirmReset && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: '16px',
                        backdropFilter: 'blur(2px)',
                        zIndex: 10
                    }}>
                        <div style={{ background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid #ef4444', textAlign: 'center', maxWidth: '80%' }}>
                            <h3 style={{ color: '#ef4444', marginBottom: '10px' }}>⚠️ Confirm Reset</h3>
                            <p style={{ marginBottom: '20px', fontSize: '14px' }}>
                                {confirmReset === 'all'
                                    ? "This will reset settings for ALL scopes (Feed, Chat, Global) to default values."
                                    : `This will reset settings for ${confirmReset.toUpperCase()} to default values.`}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                                <button
                                    onClick={() => setConfirmReset(null)}
                                    style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)', background: 'transparent', color: '#fff', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleReset(confirmReset)}
                                    style={{ padding: '8px 16px', borderRadius: '8px', border: 'none', background: '#ef4444', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                >
                                    Yes, Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

// Moved Helper OUTSIDE component to prevent re-mounting on every render
const RenderSection = ({ title, icon: Icon, scope, color, localAppearance, updateLocal, setConfirmReset }) => (
    <div style={{ marginBottom: '30px', padding: '15px', border: `1px solid ${color}`, borderRadius: '12px', background: 'rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ display: 'flex', items: 'center', gap: '8px', fontWeight: 'bold', color: color }}>
                <Icon size={18} /> {title}
            </div>
            <button
                onClick={() => setConfirmReset(scope)}
                className="btn-ghost"
                style={{ fontSize: '10px', padding: '4px 8px', color: '#999' }}
                title="Reset Defaults for this section"
            >
                <RotateCcw size={12} style={{ marginRight: '4px' }} /> Reset
            </button>
        </div>

        {/* FONT SIZE */}
        <div style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span>Text Size</span>
                <span>{localAppearance[scope].fontSize}px</span>
            </div>
            <input
                type="range"
                min="12"
                max={scope === 'feed' ? "48" : "32"}
                step="1"
                value={localAppearance[scope].fontSize}
                onChange={(e) => updateLocal(scope, 'fontSize', parseInt(e.target.value))}
                style={{ width: '100%', cursor: 'pointer', accentColor: color }}
            />
        </div>

        {/* EMOTE SIZE */}
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px', color: 'var(--text-muted)' }}>
                <span>Emote Size</span>
                <span>{localAppearance[scope].emoteSize}px</span>
            </div>
            <input
                type="range"
                min="20"
                max={scope === 'feed' ? "96" : "64"}
                step="1"
                value={localAppearance[scope].emoteSize}
                onChange={(e) => updateLocal(scope, 'emoteSize', parseInt(e.target.value))}
                style={{ width: '100%', cursor: 'pointer', accentColor: color }}
            />
        </div>
    </div>
);
